import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { money } from "@/lib/money";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import NewPacketModal from "@/components/NewPacketModal";
import { PageHeader, StatCard, DraftStatusPill, REVIEWABLE, shortDate } from "./shared";

export default function SalesPackets() {
  const { autoSend } = useOutletContext();
  const [rows, setRows] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const load = () => api.get("/sales/packets").then((r) => setRows(Array.isArray(r.data) ? r.data : []));
  useEffect(() => { load(); }, []);

  const withdraw = async (p) => {
    if (!window.confirm(`Withdraw the packet for ${p.worker_email}? It's removed from the admin review queue.`)) return;
    setBusyId(p.id);
    try { await api.delete(`/sales/packets/${p.id}`); toast.success("Packet withdrawn."); load(); }
    catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
    finally { setBusyId(null); }
  };
  const count = (s) => rows.filter((r) => r.status === s).length;

  return (
    <div data-testid="sales-packets">
      <PageHeader eyebrow="Staff your projects" title="Send Assignment Packet to Worker"
        blurb={autoSend
          ? "Put a worker on one of your projects. You set the pay terms and the assignment packet goes straight to the worker; the office gets a copy. When they sign, the assignment is created."
          : "Put a worker on one of your projects. You set the pay terms; an admin reviews and approves them before the assignment packet is emailed to the worker. When they sign, the assignment is created."}
        action={<button onClick={() => setOpenNew(true)} data-testid="sales-new-packet-btn" className="h-11 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-md flex items-center gap-2"><Plus size={18} weight="bold" /> Send assignment packet</button>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard label={autoSend ? "Awaiting approval (older)" : "Awaiting admin approval"} value={count("pending_review")} tone={count("pending_review") ? "amber" : "slate"} testId="sk-stat-pending" />
        <StatCard label="Changes requested" value={count("changes_requested")} tone={count("changes_requested") ? "amber" : "slate"} testId="sk-stat-changes" />
        <StatCard label="Out with workers" value={count("sent")} testId="sk-stat-sent" />
        <StatCard label="Signed & returned" value={count("completed")} tone="emerald" testId="sk-stat-done" />
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">Worker</th>
              <th className="text-left px-4 py-3">Project</th>
              <th className="text-left px-4 py-3">Pay rate</th>
              <th className="text-left px-4 py-3">Submitted</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No assignment packets yet — click <b>Send assignment packet</b> to staff one of your projects.</td></tr>}
            {rows.map((p) => {
              const reviewable = REVIEWABLE.includes(p.status);
              return (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`sales-packet-row-${p.id}`}>
                  <td className="px-4 py-3 font-medium">{p.worker_name || p.worker_email}{p.worker_name && <div className="text-xs text-slate-500">{p.worker_email}</div>}</td>
                  <td className="px-4 py-3">{p.project_name || p.company_name}<div className="text-xs text-slate-500">{p.company_name} · {p.location}</div></td>
                  <td className="px-4 py-3 font-mono">{money(p.pay_rate)}/hr{p.per_diem_applies ? <span className="text-xs text-slate-500 font-sans"> + PD</span> : null}</td>
                  <td className="px-4 py-3 text-slate-600">{shortDate(p.submitted_at || p.created_at)}</td>
                  <td className="px-4 py-3">
                    <DraftStatusPill doc={p} />
                    {p.assignment_id_created && <span className="ml-2 inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-emerald-100 text-emerald-800 border-emerald-300">assigned</span>}
                    {p.status === "changes_requested" && p.review_note && <div className="text-xs text-orange-800 italic mt-1 max-w-[260px] truncate" title={p.review_note}>“{p.review_note}”</div>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Link to={`/sales/packets/${p.id}`} data-testid={`sales-packet-open-${p.id}`} className="text-emerald-700 hover:text-emerald-900 font-semibold">Open</Link>
                    {reviewable && (
                      <>
                        <button onClick={() => setEditing(p)} disabled={busyId === p.id} data-testid={`sales-packet-edit-${p.id}`} title="Edit & resubmit"
                                className="ml-3 text-slate-500 hover:text-emerald-700 align-middle disabled:opacity-40"><PencilSimple size={16} weight="bold" /></button>
                        <button onClick={() => withdraw(p)} disabled={busyId === p.id} data-testid={`sales-packet-withdraw-${p.id}`} title="Withdraw"
                                className="ml-3 text-slate-400 hover:text-rose-600 align-middle disabled:opacity-40"><Trash size={16} weight="bold" /></button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {openNew && <NewPacketModal mode="sales" autoSend={autoSend} onClose={() => setOpenNew(false)} onSent={load} />}
      {editing && <NewPacketModal mode="sales" autoSend={autoSend} initial={editing} onClose={() => setEditing(null)} onSent={load} />}
    </div>
  );
}
